import { apiDelete, apiListAll, apiPatch } from '@/core/api/resources'
import { inviteCoach as gateInviteCoach, setMemberOwner } from '@/core/api/team'
import { ApiError, captureWriteIdentity } from '@/core/api/http'
import { me, updateMeProfileName, type Me } from './session'

export type Member = {
  userId: string
  vorname: string | null
  nachname: string | null
  email: string | null
  authUserId: string | null
  isOwner: boolean
  isCoach: boolean

  createdAt: string

  invitedByName: string
}

type MemberWithCreator = Omit<Member, 'invitedByName'> & { createdBy: string }

function toMember(r: Awaited<ReturnType<typeof apiListAll<'/mitgliedschaft'>>>[number]): MemberWithCreator | null {
  if (!r.members) return null
  return {
    userId: r.members.id,
    vorname: r.members.vorname,
    nachname: r.members.nachname,
    email: r.members.email,
    authUserId: r.members.auth_user_id,
    isOwner: r.is_owner ?? false,
    isCoach: r.is_coach ?? false,
    createdAt: r.created_at,
    createdBy: r.created_by,
  }
}

async function resolveInviterNames(authIds: readonly string[]): Promise<Map<string, string>> {
  const ids = [...new Set(authIds)]
  const names = new Map<string, string>()
  if (ids.length === 0) return names

  const data = await apiListAll('/members', { auth_user_id__in: ids })
  for (const u of data) {
    if (!u.auth_user_id) continue
    const name = [u.vorname, u.nachname].map((x) => (x ?? '').trim()).filter(Boolean).join(' ')
    if (name) names.set(u.auth_user_id, name)
  }
  return names
}

export async function listMembers(): Promise<Member[]> {
  const { organisationId } = await me()

  const data = await apiListAll('/mitgliedschaft', {
    organisation_id: organisationId,
    include: ['members'],
  })
  const rows = data
    .map(toMember)
    .filter((m): m is MemberWithCreator => m !== null)
  const inviterNames = await resolveInviterNames(rows.map((r) => r.createdBy))
  return rows.map(({ createdBy, ...m }) => ({ ...m, invitedByName: inviterNames.get(createdBy) ?? '—' }))
}

export async function removeMembership(userId: string): Promise<void> {
  const { organisationId } = await me()

  await apiDelete('/mitgliedschaft/{member_id}/{organisation_id}', {
    member_id: userId,
    organisation_id: organisationId,
  })
}

export type PendingMemberPatch = Partial<Pick<Member, 'vorname' | 'nachname' | 'email'>>

export async function updatePendingMember(userId: string, patch: PendingMemberPatch): Promise<void> {
  await apiPatch('/members/{id}', { id: userId }, patch)
}

export type OwnProfilePatch = Partial<Pick<Member, 'vorname' | 'nachname'>>
export type OwnProfileIdentity = Pick<Me, 'userId' | 'vorname' | 'nachname' | 'email' | 'hatPasswort'>

type ProfileName = Pick<Member, 'vorname' | 'nachname'>
type ProfileWrite = { patch: OwnProfilePatch }
type ProfileEdits = { confirmed: ProfileName; pending: ProfileWrite[]; tail: Promise<void> }
const profileEdits = new Map<string, ProfileEdits>()

export async function updateOwnProfile(patch: OwnProfilePatch, self?: OwnProfileIdentity,
  onNameChange?: (name: Pick<OwnProfileIdentity, 'vorname' | 'nachname'>) => void,
): Promise<void> {
  const { userId, vorname, nachname } = self ?? await me()
  const identity = captureWriteIdentity()
  const edits = profileEdits.get(userId) ?? { confirmed: { vorname, nachname }, pending: [], tail: Promise.resolve() }
  const write = { patch }
  edits.pending.push(write)
  profileEdits.set(userId, edits)
  const publish = () => {
    const name = edits.pending.reduce<ProfileName>((name, edit) => ({ ...name, ...edit.patch }), edits.confirmed)
    const current = { vorname: name.vorname ?? '', nachname: name.nachname ?? '' }
    updateMeProfileName(userId, current, edits.pending.length > 0)
    onNameChange?.(current)
  }
  publish()
  const saving = edits.tail.catch(() => {}).then(async () => {
    try {
      await apiPatch('/members/{id}', { id: userId }, patch, identity)
      edits.confirmed = { ...edits.confirmed, ...patch }
    } finally {
      edits.pending = edits.pending.filter((entry) => entry !== write)
      publish()
      if (edits.pending.length === 0) profileEdits.delete(userId)
    }
  })
  edits.tail = saving
  await saving
}

export type InviteCoachInput = { vorname: string; nachname: string; email: string }

export async function inviteCoach(input: InviteCoachInput): Promise<void> {
  const { organisationId } = await me()

  await gateInviteCoach({ ...input, organisationId })
}

const OWNER_SWITCH_REFUSALS: Record<number, string> = {
  403: 'Nur Owner dieser Organisation können Rollen ändern.',
  409: 'Die Organisation braucht mindestens einen Owner.',
}

export async function setOwner(userId: string, isOwner: boolean): Promise<void> {
  const { organisationId } = await me()
  try {
    await setMemberOwner({ memberId: userId, organisationId, isOwner })
  } catch (e) {
    if (e instanceof ApiError) {
      throw new Error(OWNER_SWITCH_REFUSALS[e.status] ?? 'Die Rolle konnte nicht geändert werden.')
    }
    throw e
  }
}
